import { useState, useEffect, useCallback } from 'react';
import { Key, Copy, Check, Trash2, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import {
  getProject,
  listKeys,
  createKey,
  revokeKey,
  createCheckoutSession,
  type DeveloperProject,
  type ApiKeySummary,
} from '@/services/developerApi';

interface Props {
  isSupabaseReady: boolean;
}

export function DeveloperApiSection({ isSupabaseReady }: Props) {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [project, setProject] = useState<DeveloperProject | null>(null);
  const [keys, setKeys] = useState<ApiKeySummary[]>([]);

  const [label, setLabel] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [newSecretCopied, setNewSecretCopied] = useState(false);

  const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);

  const isPaid = project?.plan_code === 'developer';
  const maxKeys = isPaid ? 3 : 1;
  const activeKeys = keys.filter((k) => !k.revoked_at);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const [proj, keyList] = await Promise.all([getProject(), listKeys()]);
      setProject(proj);
      setKeys(keyList);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load developer settings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSupabaseReady) void loadData();
  }, [isSupabaseReady, loadData]);

  const handleCreateKey = async () => {
    try {
      setIsCreating(true);
      setCreateError(null);
      setNewSecret(null);
      setNewSecretCopied(false);
      const result = await createKey(label.trim());
      setNewSecret(result.key);
      setLabel('');
      await loadData();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create key');
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    try {
      setRevokingKeyId(keyId);
      await revokeKey(keyId);
      await loadData();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to revoke key');
      await loadData();
    } finally {
      setRevokingKeyId(null);
    }
  };

  const handleUpgrade = async () => {
    try {
      setIsStartingCheckout(true);
      setCreateError(null);
      const url = await createCheckoutSession();
      window.location.assign(url);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to start checkout');
      setIsStartingCheckout(false);
    }
  };

  const handleCopySecret = async () => {
    if (!newSecret) return;
    try {
      await navigator.clipboard.writeText(newSecret);
      setNewSecretCopied(true);
      setTimeout(() => setNewSecretCopied(false), 3000);
    } catch {
      // Keep secret visible for manual copy
    }
  };

  if (!isSupabaseReady) {
    return (
      <div className="surface-panel mt-4">
        <div className="page-eyebrow mb-3">Developer API</div>
        <p className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
          Connecting to account... If this persists, try refreshing the page.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="surface-panel mt-4">
        <div className="page-eyebrow mb-3">Developer API</div>
        <div className="flex items-center gap-2 py-4">
          <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--text-tertiary)' }} />
          <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
            Loading...
          </span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="surface-panel mt-4">
        <div className="page-eyebrow mb-3">Developer API</div>
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4" style={{ color: '#dc2626' }} />
          <span className="type-caption" style={{ color: '#dc2626' }}>
            {loadError}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="surface-panel mt-4">
      <div className="page-eyebrow mb-3">Developer API</div>

      {/* Plan Summary */}
      <div
        className="flex items-center justify-between flex-wrap gap-2 p-3 rounded-lg mb-4"
        style={{ border: '1px solid var(--border-primary)', background: 'var(--bg-secondary)' }}
      >
        <div className="flex items-center gap-2">
          <span
            className="type-label px-2 py-0.5 rounded-full"
            style={{
              background: isPaid ? '#dbeafe' : '#f3f4f6',
              color: isPaid ? '#1d4ed8' : 'var(--text-secondary)',
              fontSize: '12px',
            }}
          >
            {isPaid ? 'Developer' : 'Free'}
          </span>
          <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
            {isPaid
              ? `${(project?.monthly_request_limit ?? 50000).toLocaleString()} req/mo · ${project?.history_days ?? 90}d history`
              : '100 req/day · 14d history · No entities or thesis'}
          </span>
        </div>
        {!isPaid && (
          <button
            className="type-label px-3 py-1.5 rounded-md"
            style={{
              background: 'var(--accent-primary)',
              color: 'white',
              opacity: isStartingCheckout ? 0.6 : 1,
            }}
            disabled={isStartingCheckout}
            onClick={handleUpgrade}
          >
            {isStartingCheckout ? 'Redirecting...' : 'Upgrade to Developer — $10/mo'}
          </button>
        )}
      </div>

      {/* One-Time Secret Notice */}
      {newSecret && (
        <div
          className="p-3 rounded-lg mb-4"
          style={{ border: '2px solid #16a34a', background: '#f0fdf4' }}
        >
          <div className="flex items-start gap-2 mb-2">
            <Key className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#16a34a' }} />
            <div className="flex-1 min-w-0">
              <p className="type-label" style={{ color: '#15803d', margin: 0 }}>
                Your new API key — save it now
              </p>
              <p className="type-caption" style={{ color: '#16a34a', margin: '2px 0 0' }}>
                This key cannot be shown again.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <code
              className="type-body flex-1 min-w-0 px-2 py-1 rounded"
              style={{
                background: 'white',
                border: '1px solid #bbf7d0',
                wordBreak: 'break-all',
                fontSize: '12px',
                fontFamily: 'monospace',
              }}
            >
              {newSecret}
            </code>
            <button
              className="flex items-center gap-1 px-2 py-1 rounded type-caption"
              style={{
                background: newSecretCopied ? '#16a34a' : 'var(--accent-primary)',
                color: 'white',
                whiteSpace: 'nowrap',
              }}
              onClick={handleCopySecret}
            >
              {newSecretCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {newSecretCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* Generate Key */}
      <div className="mb-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="type-caption block mb-1" style={{ color: 'var(--text-tertiary)' }}>
              Key label
            </label>
            <input
              type="text"
              className="type-body w-full px-2.5 py-1.5 rounded-md"
              style={{
                border: '1px solid var(--border-primary)',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                outline: 'none',
              }}
              placeholder="e.g. production-agent"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={isCreating}
            />
          </div>
          <button
            className="type-label px-3 py-1.5 rounded-md flex items-center gap-1.5"
            style={{
              background: 'var(--accent-primary)',
              color: 'white',
              opacity: isCreating || activeKeys.length >= maxKeys ? 0.5 : 1,
              whiteSpace: 'nowrap',
            }}
            disabled={isCreating || activeKeys.length >= maxKeys}
            onClick={handleCreateKey}
          >
            {isCreating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Key className="h-3.5 w-3.5" />
            )}
            Generate Key
          </button>
        </div>
        {activeKeys.length >= maxKeys && (
          <p className="type-caption mt-1" style={{ color: 'var(--text-tertiary)' }}>
            Key limit reached ({maxKeys} for {isPaid ? 'Developer' : 'Free'} plan). Revoke an
            existing key to create a new one.
          </p>
        )}
        {createError && (
          <p className="type-caption mt-1" style={{ color: '#dc2626' }}>
            {createError}
          </p>
        )}
      </div>

      {/* API Key List */}
      {keys.length === 0 ? (
        <p className="type-caption py-3" style={{ color: 'var(--text-tertiary)' }}>
          No API keys yet. Generate one above to get started.
        </p>
      ) : (
        <div className="space-y-2">
          {keys.map((k) => (
            <div
              key={k.id}
              className="flex items-center justify-between flex-wrap gap-2 px-3 py-2 rounded-md"
              style={{
                border: '1px solid var(--border-primary)',
                opacity: k.revoked_at ? 0.5 : 1,
              }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <code
                  className="type-caption"
                  style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}
                >
                  {k.key_prefix}...
                </code>
                <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
                  {k.label}
                </span>
                {k.revoked_at && (
                  <span
                    className="type-caption px-1.5 py-0.5 rounded"
                    style={{ background: '#fef2f2', color: '#dc2626', fontSize: '10px' }}
                  >
                    Revoked
                  </span>
                )}
              </div>
              {!k.revoked_at && (
                <button
                  className="type-caption flex items-center gap-1 px-2 py-1 rounded"
                  style={{ color: '#dc2626', opacity: revokingKeyId === k.id ? 0.5 : 1 }}
                  disabled={revokingKeyId === k.id}
                  onClick={() => handleRevokeKey(k.id)}
                >
                  <Trash2 className="h-3 w-3" />
                  {revokingKeyId === k.id ? 'Revoking...' : 'Revoke'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Quick Start */}
      <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border-primary)' }}>
        <p className="type-label mb-1.5" style={{ color: 'var(--text-primary)' }}>
          Quick start
        </p>
        <code
          className="type-caption block p-2 rounded"
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            fontFamily: 'monospace',
            fontSize: '11px',
            color: 'var(--text-secondary)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {`curl https://personalinsights.app/api/v1/feed \\\n  -H "Authorization: Bearer YOUR_API_KEY"`}
        </code>
        <a
          href="https://github.com/devp1/Personal_Insights/blob/main/mcp/README.md"
          target="_blank"
          rel="noopener noreferrer"
          className="type-caption inline-flex items-center gap-1 hover:underline mt-2"
          style={{ color: 'var(--accent-primary)' }}
        >
          Full API documentation
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
